import React, { useState, useEffect } from "react";
import axios from "axios";
import { BACKEND_URL_OUT, makeRequestAuth } from "../../../_config/api";
import { ToastContainer } from "react-toastify";
import { showSuccessToast, showErrorToast } from "../../../utils/toastUtils";
import {
  ChevronRight
} from "lucide-react";


const LANGUAGES = ["English", "Hindi", "Punjabi", "French", "Spanish", "Arabic", "Chinese", "Russian"];

const getEmptyBlock = () => ({
  language: "",
  description: "",
  files: [],
});

const StatementForm = () => {
  const [statements, setStatements] = useState([getEmptyBlock()]);
  const [narratives, setNarratives] = useState([getEmptyBlock()]);
  const [eSignatureText, setESignatureText] = useState("");
  const [signatureUrl, setSignatureUrl] = useState(null);
  const [signatureUploading, setSignatureUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { fetchStatement(); }, []);

  const uploadFile = async (file) => {
    try {
      if (!file) {
        showErrorToast("No file selected");
        return null;
      }

      const token = localStorage.getItem("token");

      if (!token) {
        showErrorToast("Session expired. Please login again.");
        return null;
      }

      /* 🔥 Optional but VERY recommended */
      const MAX_SIZE = 5 * 1024 * 1024; // 5MB

      if (file.size > MAX_SIZE) {
        showErrorToast("File too large. Max size is 5MB.");
        return null;
      }

      const allowedTypes = [
        "image/jpeg",
        "image/png",
        "application/pdf",
      ];

      if (!allowedTypes.includes(file.type)) {
        showErrorToast("Only JPG, PNG, and PDF files are allowed.");
        return null;
      }

      const formData = new FormData();
      formData.append("file", file);

      const res = await axios.post(
        `${BACKEND_URL_OUT}/uploadImage`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          timeout: 20000, // ⭐ prevents hanging requests
        }
      );

      showSuccessToast("File uploaded successfully ✅");

      return {
        url: res.data.data.imageUrl,
        fileName: file.name,
      };

    } catch (error) {

      /* ⭐ Axios Error Handling */
      if (error.response) {

        const status = error.response.status;

        switch (status) {

          case 401:
            showErrorToast("Session expired. Please login again.");
            localStorage.removeItem("token");
            window.location.href = "/login";
            break;

          case 413:
            showErrorToast("File is too large.");
            break;

          case 415:
            showErrorToast("Unsupported file type.");
            break;

          case 500:
            showErrorToast("Server error. Try again later.");
            break;

          default:
            showErrorToast(
              error.response?.data?.message ||
              "Upload failed. Please try again."
            );
        }

      } else if (error.request) {

        // request made but no response
        showErrorToast("Network error. Check your internet connection.");

      } else {

        // something else
        showErrorToast("Unexpected error occurred.");
      }

      console.error("UPLOAD ERROR:", error);

      return null;
    }
  };


  const uploadSignature = async (file) => {
    if (!file) return;
    setSignatureUploading(true);
    try {
      const uploaded = await uploadFile(file);
      if (!uploaded) return;
      setSignatureUrl(uploaded.url);
      showSuccessToast("Signature uploaded ✅");
    } catch {
      showErrorToast("Signature upload failed ❌");
    } finally {
      setSignatureUploading(false);
    }
  };

  const updateList = (setter) => (index, field, value) => {
    setter((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleFileUpload = async (file, index, setter) => {
    if (!file) return;

    try {
      const uploaded = await uploadFile(file);

      // ✅ STOP if upload failed
      if (!uploaded) return;

      setter((prev) => {
        const updated = [...prev];
        updated[index].files = [...(updated[index].files || []), uploaded];
        return updated;
      });

      showSuccessToast("File uploaded ✅");

    } catch {
      showErrorToast("Upload failed ❌");
    }
  };


  const removeFile = (index, fileIndex, setter) => {
    setter((prev) => {
      const updated = [...prev];
      updated[index].files.splice(fileIndex, 1);
      return [...updated];
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {

      const cleanFiles = (files = []) =>
        files.map(({ url, fileName }) => ({
          url,
          fileName
        }));

      const cleanBlocks = (blocks = []) =>
        blocks.map(({ language, description, files }) => ({
          language,
          description,
          files: cleanFiles(files)
        }));

      const payload = {
        statements: cleanBlocks(statements),
        narratives: cleanBlocks(narratives),
        eSignatureText,
        signatureFile: signatureUrl
      };

      // const payload = { statements, narratives, eSignatureText, signatureFile: signatureUrl };
      const res = await makeRequestAuth("post", "/user/createOrUpdateStatement", payload);
      setPreview(res?.data);
      showSuccessToast("Statement saved successfully ✅");
    } catch (err) {
      showErrorToast(err?.response?.data?.message || "Error saving statement");
    } finally {
      setLoading(false);
    }
  };

  const fetchStatement = async () => {
    try {
      const res = await makeRequestAuth("get", "/user/getStatement");
      if (res?.data) {
        setStatements(res.data.statements?.length ? res.data.statements : [getEmptyBlock()]);
        setNarratives(res.data.narratives?.length ? res.data.narratives : [getEmptyBlock()]);
        setESignatureText(res.data?.eSignatureText || "");
        setSignatureUrl(res.data?.signatureFile || null);
        setPreview(res.data);
      }
    } catch { console.log("No statement found"); }
  };

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 lg:p-8 antialiased">
      <ToastContainer />

      {/* Header */}
      <div className="mb-8 pb-6 border-b border-slate-200">
        <h1 className="text-2xl font-bold text-slate-800 tracking-tight">Statement & Narratives</h1>
        <p className="text-slate-500 text-sm">Provide your official declarations and supporting documents.</p>
      </div>

      {loading && (
        <div className="fixed inset-0 bg-slate-900/20 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white px-8 py-4 rounded-xl shadow-2xl flex items-center gap-3 font-medium animate-bounce">
            Processing...
          </div>
        </div>
      )}

      {!preview ? (
        <form onSubmit={handleSubmit} className="space-y-10">

          <DynamicSection
            title="Official Statements"
            subtitle="Declare your formal statements in your preferred language."
            list={statements}
            update={updateList(setStatements)}
            add={() => setStatements([...statements, getEmptyBlock()])}
            remove={(i) => setStatements(statements.filter((_, idx) => idx !== i))}
            handleFileUpload={(file, i) => handleFileUpload(file, i, setStatements)}
            removeFile={(i, fi) => removeFile(i, fi, setStatements)}
          />

          <DynamicSection
            title="Supporting Narratives"
            subtitle="Additional stories or background information to support your case."
            list={narratives}
            update={updateList(setNarratives)}
            add={() => setNarratives([...narratives, getEmptyBlock()])}
            remove={(i) => setNarratives(narratives.filter((_, idx) => idx !== i))}
            handleFileUpload={(file, i) => handleFileUpload(file, i, setNarratives)}
            removeFile={(i, fi) => removeFile(i, fi, setNarratives)}
          />

          {/* SIGNATURE SECTION */}
          <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
            <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
              <span className="p-1.5 bg-slate-100 rounded-md">🖋️</span>
              Final Authorization
            </h3>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <Input label="E-Signature (Type Full Name) *" value={eSignatureText} onChange={setESignatureText} placeholder="John Doe" />
              </div>
              <div className="space-y-4">
                <label className="block text-sm font-semibold text-slate-700">Upload Signature File *</label>
                <div className="border-2 border-dashed border-slate-200 rounded-xl p-4 flex flex-col items-center justify-center bg-slate-50 hover:bg-slate-100 transition-colors cursor-pointer relative">
                  <input type="file" className="absolute inset-0 opacity-0 cursor-pointer" onChange={(e) => uploadSignature(e.target.files[0])} />
                  {signatureUrl ? (
                    <img src={signatureUrl} alt="signature" className="h-16 object-contain" />
                  ) : (
                    <div className="text-center">
                      <span className="text-slate-400 text-xs">Click to upload image</span>
                    </div>
                  )}
                </div>
                {signatureUploading && <p className="text-xs text-blue-500 animate-pulse">Uploading signature...</p>}
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-6">
            <button className="bg-[#132b45] hover:bg-[#1a3a5c] text-white px-12 py-3.5 rounded-lg font-bold shadow-lg transition-all active:scale-95">
              Submit Statement
            </button>
          </div>
        </form>
      ) : (
        <div className="space-y-8 animate-in fade-in zoom-in-95 duration-500">
          {/* Status Header */}
          <div className="flex items-center justify-between bg-white border border-slate-200 p-6 rounded-2xl shadow-sm">
            <div>
              <h2 className="text-xl font-bold text-slate-800">Submission Preview</h2>
              <p className="text-slate-500 text-sm">Review your submitted declarations and documents.</p>
            </div>
            <div className={`px-4 py-2 rounded-full border text-xs font-bold uppercase tracking-widest ${preview.status === 'pending' ? 'bg-amber-50 text-amber-600 border-amber-200' :
              preview.status === 'approve' ? 'bg-emerald-50 text-emerald-600 border-emerald-200' :
                'bg-rose-50 text-rose-600 border-rose-200'
              }`}>
              ● {preview.status || 'Pending'}
            </div>
          </div>

          {/* Statements Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-bold text-slate-700 ml-1 italic">Official Statements</h3>
            {preview.statements.map((st, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
                <div className="bg-slate-50 px-6 py-3 border-b border-slate-200 flex justify-between">
                  <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Statement #{i + 1}</span>
                  <span className="text-xs font-semibold text-[#132b45] bg-blue-50 px-2 py-0.5 rounded">{st.language}</span>
                </div>
                <div className="p-6 space-y-4">
                  <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">{st.description}</p>
                  <div className="flex flex-wrap gap-2 pt-2">
                    {st.files.map((file, fi) => (
                      <a key={fi} href={file.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 bg-slate-100 hover:bg-slate-200 px-3 py-2 rounded-lg text-xs font-medium text-slate-600 transition-colors border border-slate-200">
                        <span className="text-blue-500">📄</span> {file.fileName}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Narratives Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-bold text-slate-700 ml-1 italic">Supporting Narratives</h3>
            {preview.narratives.map((nr, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
                <div className="bg-slate-50 px-6 py-3 border-b border-slate-200 flex justify-between">
                  <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Narrative #{i + 1}</span>
                  <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded">{nr.language}</span>
                </div>
                <div className="p-6 space-y-4">
                  <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">{nr.description}</p>
                  <div className="flex flex-wrap gap-2 pt-2">
                    {nr.files.map((file, fi) => (
                      <a key={fi} href={file.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 bg-slate-100 hover:bg-slate-200 px-3 py-2 rounded-lg text-xs font-medium text-slate-600 transition-colors border border-slate-200">
                        <span className="text-emerald-500">📎</span> {file.fileName}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Authorization Section */}
          <div className="bg-[#132b45] text-white rounded-2xl p-8 shadow-xl relative overflow-hidden">
            <div className="relative z-10 flex flex-col md:flex-row justify-between items-center gap-8">
              <div className="space-y-2 text-center md:text-left">
                <p className="text-blue-200 text-xs font-bold uppercase tracking-widest">E-Signature Verification</p>
                <h4 className="text-2xl font-serif italic tracking-wide">{preview.eSignatureText}</h4>
                <p className="text-blue-300 text-[10px]">Digitally signed on {new Date(preview.createdAt).toLocaleString()}</p>
              </div>
              <div className="bg-white p-2 rounded-xl shadow-inner">
                <img src={preview.signatureFile} alt="Signature" className="h-20 w-auto object-contain brightness-90 contrast-125" />
              </div>
            </div>
            {/* Abstract Background Decoration */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -mr-16 -mt-16"></div>
          </div>

          {/* Edit Button (Visible if rejected or just to go back) */}
          {preview.status === 'reject' && (
            <>

              <button
                onClick={() => setPreview(null)}
                className="group flex items-center gap-3 bg-[#132b45] text-white px-10 py-4 rounded-2xl font-bold text-xs uppercase tracking-widest hover:bg-black transition-all shadow-xl hover:shadow-2xl"
              >
                Modify Records <ChevronRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const DynamicSection = ({ title, subtitle, list, update, add, remove, handleFileUpload, removeFile }) => (
  <div className="space-y-6">
    <div>
      <h3 className="text-lg font-bold text-slate-800">{title}</h3>
      <p className="text-slate-500 text-sm">{subtitle}</p>
    </div>

    {list.map((item, index) => (
      <div key={index} className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm relative group animate-in slide-in-from-left-4 duration-300">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="md:col-span-1">
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Language</label>
            <select
              value={item.language}
              onChange={(e) => update(index, "language", e.target.value)}
              className="w-full border-slate-300 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-slate-900/10 outline-none bg-white transition-all shadow-sm"
            >
              <option value="">Select</option>
              {LANGUAGES.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>

          <div className="md:col-span-3">
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Description (Max 5000 chars)</label>
            <textarea
              value={item.description}
              rows="4"
              onChange={(e) => update(index, "description", e.target.value)}
              className="w-full border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-slate-900/10 outline-none transition-all shadow-sm"
              placeholder="Describe your statement here..."
            />
          </div>
        </div>

        <div className="mt-6">
          <label className="block text-sm font-semibold text-slate-700 mb-3">Supporting Documents</label>
          <div className="flex flex-wrap gap-3">
            <div className="relative overflow-hidden bg-slate-100 hover:bg-slate-200 border border-slate-200 rounded-lg px-4 py-2 transition-all cursor-pointer">
              <span className="text-sm font-bold text-slate-600">+ Upload File</span>
              <input type="file" className="absolute inset-0 opacity-0 cursor-pointer" onChange={(e) => handleFileUpload(e.target.files[0], index)} />
            </div>

            {item.files?.filter(Boolean).map((f, i) => (
              <div key={i} className="flex items-center gap-2 bg-slate-800 text-white px-3 py-1.5 rounded-lg text-xs font-medium group/file shadow-sm">
                <span className="truncate max-w-[150px] cursor-pointer" onClick={() => window.open(f.url)}>{f.fileName}</span>
                <button type="button" onClick={() => removeFile(index, i)} className="hover:text-rose-400 transition-colors ml-1 font-bold">×</button>
              </div>
            ))}
          </div>
        </div>

        {list.length > 1 && (
          <button type="button" onClick={() => remove(index)} className="absolute -top-3 -right-3 w-8 h-8 bg-white border border-rose-100 text-rose-500 rounded-full shadow-md hover:bg-rose-500 hover:text-white transition-all flex items-center justify-center font-bold">
            ×
          </button>
        )}
      </div>
    ))}

    <button type="button" onClick={add} className="w-full py-3 border-2 border-dashed border-slate-200 rounded-xl text-slate-500 font-bold hover:bg-slate-50 hover:border-slate-300 transition-all flex items-center justify-center gap-2">
      <span className="text-xl">+</span> Add {title}
    </button>
  </div>
);

const Input = ({ label, value, onChange, placeholder }) => (
  <div className="w-full">
    <label className="block text-sm font-semibold text-slate-700 mb-1.5">{label}</label>
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-slate-900/10 outline-none transition-all shadow-sm"
    />
  </div>
);

export default StatementForm;